import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Heart, MapPin, Trophy, X, Calendar, Sparkles } from "lucide-react";
import { api } from "@/lib/api";

/**
 * /good-deeds — public wall of every approved community-mission proof.
 *
 * Auto-refreshes every 30s so when an admin approves a new one, the wall
 * picks it up. Cards are clickable and open a full-detail modal showing
 * the full photo, full caption, and the linked mission story.
 */
export default function GoodDeedsPage() {
  const [proofs, setProofs] = useState([]);
  const [stats, setStats] = useState(null);
  const [active, setActive] = useState(null);   // currently expanded proof

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      api.get("/missions/feed?limit=80").then(({ data }) => { if (!cancelled) setProofs(data?.proofs || []); }).catch(() => {});
      api.get("/missions/stats").then(({ data }) => { if (!cancelled) setStats(data); }).catch(() => {});
    };
    load();
    const id = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Body scroll lock while modal open
  useEffect(() => {
    if (active) document.body.classList.add("overflow-hidden");
    else document.body.classList.remove("overflow-hidden");
    return () => document.body.classList.remove("overflow-hidden");
  }, [active]);

  const backendBase = process.env.REACT_APP_BACKEND_URL || "";
  const photoUrl = (u) => (!u ? null : (u.startsWith("http") ? u : `${backendBase}${u}`));

  return (
    <div className="splash-root" data-testid="good-deeds-page">
      <section className="splash-hero relative">
        <div className="splash-hero-overlay" aria-hidden="true" />
        <div className="splash-grain" aria-hidden="true" />
        <div className="relative z-10 max-w-5xl mx-auto px-4 md:px-8 pt-10 md:pt-16 pb-12">
          <Link to="/" className="splash-mono uppercase tracking-[0.22em] text-[11px] text-[#76B82A] inline-flex items-center gap-2 hover:text-[#84d130]">
            <ArrowLeft size={12} /> Back to The Local Loot
          </Link>
          <div className="mt-8 splash-mono uppercase tracking-[0.22em] text-[11px] text-[#d7a62a]">
            The Good Deeds wall
          </div>
          <h1 className="splash-display text-5xl md:text-7xl uppercase mt-3 leading-[0.95]">
            <span className="block headline-stamp">Good people.</span>
            <span className="block headline-stamp--green mt-1 md:mt-2">Good deeds.</span>
          </h1>
          {stats && (
            <div className="mt-6 flex flex-wrap gap-3">
              <span className="live-chip-light">
                <Heart size={12} /> {stats.good_deeds_total} good deeds done
              </span>
              {stats.loot_legends_total > 0 && (
                <span className="live-chip-light" style={{ borderColor: "rgba(215,166,42,0.55)", background: "rgba(215,166,42,0.12)", color: "#d7a62a" }}>
                  <Trophy size={12} /> {stats.loot_legends_total} Loot Legends crowned
                </span>
              )}
            </div>
          )}
          <Link to="/missions" className="btn-loot-solid mt-6 inline-flex">
            Join a mission
          </Link>
        </div>
      </section>

      <section className="cream-section py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          {proofs.length === 0 ? (
            <div className="text-center py-12 text-[#0d100d]/55">
              No good deeds yet — be the first.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {proofs.map((p, i) => (
                <button
                  type="button"
                  key={p.id || i}
                  onClick={() => setActive(p)}
                  className="winner-card text-left cursor-pointer hover:-translate-y-1 transition-transform focus:outline-none focus:ring-2 focus:ring-[#76B82A] focus:ring-offset-2 focus:ring-offset-[#fffaef]"
                  aria-label={`Open ${p.name || p.username || "hunter"}'s good deed`}
                  data-testid={`good-deed-card-${i}`}
                >
                  <div
                    className="winner-card-photo"
                    style={p.photo_url ? { backgroundImage: `url(${photoUrl(p.photo_url)})` } : {}}
                  >
                    {!p.photo_url && (
                      <div className="absolute inset-0 flex items-center justify-center splash-display text-3xl text-[#f7f3e6]/65">
                        <Heart size={32} />
                      </div>
                    )}
                    <div className="absolute top-2 left-2 px-1.5 py-0.5 bg-[#4f8c19] text-[#f7f3e6] splash-mono text-[10px] uppercase tracking-widest inline-flex items-center gap-1">
                      <Heart size={10} /> Good deed
                    </div>
                  </div>
                  <div className="px-3 py-3">
                    <div className="splash-display text-base uppercase leading-tight">
                      {(p.name || p.username || "Hunter")}
                    </div>
                    {p.city && (
                      <div className="splash-mono text-[11px] text-[#0d100d]/60 mt-0.5 inline-flex items-center gap-1">
                        <MapPin size={10} /> {p.city}
                      </div>
                    )}
                    {p.mission_title && (
                      <div className="text-[12px] text-[#4f8c19] mt-2 leading-tight">
                        {p.mission_title}
                      </div>
                    )}
                    {p.caption && (
                      <p className="text-[12px] text-[#0d100d]/70 mt-1 leading-snug line-clamp-3">
                        “{p.caption}”
                      </p>
                    )}
                    <div className="mt-2 splash-mono uppercase tracking-widest text-[10px] text-[#76B82A]">
                      Tap to read more →
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      <DeedModal
        proof={active}
        onClose={() => setActive(null)}
        photoUrl={photoUrl}
      />

      <footer className="splash-footer py-8 text-center">
        <div className="max-w-7xl mx-auto px-4 md:px-8 splash-mono text-[12px] text-[#f7f3e6]/55">
          © The Local Loot · Live Life. Do Good. Be Happy.
        </div>
      </footer>
    </div>
  );
}

/**
 * Full-detail modal — shown when a hunter taps a good-deed card.
 *
 * Renders the photo at full aspect, the hunter's full caption, the
 * mission card it belongs to, the city, the date approved, and a link
 * back to the mission so the next hunter can pick it up. Closes on
 * backdrop click, ESC, or close button.
 */
function DeedModal({ proof, onClose, photoUrl }) {
  // ESC closes
  useEffect(() => {
    if (!proof) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [proof, onClose]);

  if (!proof) return null;

  const photo = photoUrl(proof.photo_url);
  const date = proof.approved_at || proof.created_at;
  const dateLabel = date ? new Date(date).toLocaleDateString(undefined, {
    year: "numeric", month: "short", day: "numeric",
  }) : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/80 backdrop-blur-sm p-0 md:p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="good-deed-modal-title"
      data-testid="good-deed-modal"
    >
      <div
        className="bg-[#0d100d] border-2 border-[#76B82A]/40 max-w-2xl w-full max-h-[92vh] overflow-y-auto md:rounded-none"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-3 sticky top-0 bg-[#0d100d] border-b border-[#F5F2E8]/10 z-10">
          <div className="splash-mono uppercase tracking-[0.22em] text-[10px] text-[#76B82A] inline-flex items-center gap-2">
            <Heart size={12} fill="#76B82A" /> Good Deed
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1 -m-1 text-[#F5F2E8]/70 hover:text-[#F5F2E8]"
            data-testid="good-deed-modal-close"
          >
            <X size={22} />
          </button>
        </div>

        {photo && (
          <div className="bg-black">
            <img
              src={photo}
              alt={`${proof.name || proof.username || "Hunter"} good deed`}
              className="w-full max-h-[55vh] object-contain"
            />
          </div>
        )}

        <div className="px-5 py-5">
          <h2
            id="good-deed-modal-title"
            className="splash-display uppercase text-3xl md:text-4xl text-[#fffaef] leading-tight"
          >
            {(proof.name || proof.username || "Hunter")}
          </h2>
          <div className="mt-2 flex flex-wrap gap-3 splash-mono uppercase tracking-widest text-[10px] text-[#f7f3e6]/65">
            {proof.city && (
              <span className="inline-flex items-center gap-1"><MapPin size={10} /> {proof.city}</span>
            )}
            {dateLabel && (
              <span className="inline-flex items-center gap-1"><Calendar size={10} /> {dateLabel}</span>
            )}
            {proof.mission_title && (
              <span className="inline-flex items-center gap-1 text-[#76B82A]">
                <Sparkles size={10} /> {proof.mission_title}
              </span>
            )}
          </div>

          {proof.caption && (
            <blockquote className="mt-5 border-l-2 border-[#76B82A] pl-4 text-[15px] md:text-base text-[#f7f3e6]/85 leading-relaxed italic whitespace-pre-wrap">
              “{proof.caption}”
            </blockquote>
          )}

          {proof.mission_id && (
            <Link
              to="/missions"
              onClick={onClose}
              className="btn-loot-solid mt-6 inline-flex"
              data-testid="good-deed-modal-mission-cta"
            >
              Take this mission too
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
